import {
    Box,
    Button,
    FormControl,
    InputLabel,
    Menu,
    MenuItem,
    Typography,
} from "@material-ui/core";
import { useCallback, useRef, useEffect, useState } from "react";
import { LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "react-toastify/dist/ReactToastify.css";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import { format } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import { Link as ReactLink } from "react-router-dom";
import { getRegistrationAuditReport, getTransactionReport } from "../../api/transactions";

const useStyles = makeStyles((theme) => ({
    formControl: {
        marginRight: theme.spacing(1),
        minWidth: 120,
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
}));

LicenseManager.setLicenseKey(
    "Using_this_{AG_Grid}_Enterprise_key_{AG-062453}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Lucid_USA,_Inc}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{V360}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{V360}_need_to_be_licensed___{V360}_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_August_2025}____[v3]_[01]_MTc1NjMzNTYwMDAwMA==83cd4edac4587e17c3187cf63f89d914",
);

function RegistrationStatusReporting() {
    const gridRef = useRef();
    const [gridApi, setGridApi] = useState(null);
    const [perPage, setPerPage] = useState(25);
    const classes = useStyles();

    const columnDefs = [
        {
            headerName: "Order ID",
            field: "orderId",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Quote Name",
            field: "quoteName",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "VIN",
            field: "vin",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Status Before",
            field: "statusBefore",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Status After",
            field: "statusAfter",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Status Change DateTime",
            field: "statusChangedDateTime",
            filter: "agDateColumnFilter",
            filterParams: {
                filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
            },
            sortable: true,
            valueFormatter: (params) => {
                return params.value
                    ? format(new Date(params.value), "MMM dd, yyyy hh:mm a")
                    : params.value;
            },
        },
        {
            headerName: "Process Name",
            field: "processName",
            filter: "agTextColumnFilter",
        },
    ];

    const defaultColDef = {
        sortable: false,
        filter: true,
        floatingFilter: true,
        editable: false,
        resizable: true,
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    useEffect(() => {
        if (!gridApi) {
            return;
        }

        const dataSource = {
            getRows: async (params) => {
                const page = Math.floor(params.request.startRow / perPage) + 1;
                const payload = {
                    pageNumber: page,
                    pageLimit: perPage,
                };

                if (params.request.filterModel) {
                    payload["filterModel"] = params.request.filterModel;
                }
                if (params.request.sortModel) {
                    payload["sortModel"] = params.request.sortModel;
                }

                try {
                    const { data, count } = await getRegistrationAuditReport(payload);
                    const lastRow =
                        count <= params.request.endRow ? count : undefined;
                    params.success({
                        rowData: data,
                        rowCount: lastRow,
                    });
                } catch (error) {
                    params.fail();
                }
            },
        };

        gridApi.setGridOption("serverSideDatasource", dataSource);
    }, [gridApi, perPage]);

    const handlePerPageChange = (e) => {
        setPerPage(e.target.value);
    };

    const [anchorEl, setAnchorEl] = useState(null);

    const handleActionClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleActionClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const onFirstDataRendered = useCallback(() => {
        gridRef.current.api.sizeColumnsToFit();
    }, []);

    const onBtExport = useCallback(() => {
        gridRef.current.api.exportDataAsExcel();
    }, []);

    const onBtnCSVExport = useCallback(() => {
        gridRef.current.api.exportDataAsCsv();
    }, []);

    const onBtnCSVExportAll = useCallback(() => {
        const payload = { pullAll: true };
        getTransactionReport(payload)
            .then((jsonData) => {
                const csvData = jsonToCsv(jsonData.data);
                const blob = new Blob([csvData], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "data.csv";
                document.body.appendChild(a);
                a.click();
            })
            .catch((error) => console.error(error));
    }, []);

    const jsonToCsv = (jsonData) => {
        let csv = "";
        const columnsHeaders = columnDefs.map((column) => column.headerName);
        const headers = Object.keys(jsonData[0]);
        csv += columnsHeaders.join(",") + "\n";
        jsonData.forEach(function (row) {
            const data = headers
                .map((header) => JSON.stringify(row[header]))
                .join(",");
            csv += data + "\n";
        });
        return csv;
    };

    return (
        <>
            <div>
                <div style={{ margin: "10px 20px" }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <ReactLink
                            color="inherit"
                            to="/"
                            className="breadcrumb-link"
                        >
                            Dashboard
                        </ReactLink>
                        <ReactLink
                            color="inherit"
                            to="/reporting/"
                            className="breadcrumb-link"
                        >
                            Reporting
                        </ReactLink>
                        <Typography color="textPrimary">
                            Registration Audit Reporting
                        </Typography>
                    </Breadcrumbs>
                </div>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    style={{ margin: "10px 20px" }}
                >
                    <div>
                        <Typography
                            variant="h5"
                            component={"h5"}
                            style={{
                                fontFamily: "GTAmericaMedium",
                                fontSize: "24px",
                                lineHeight: "120%",
                                fontWeight: "500",
                            }}
                        >
                            Registration Audit Reporting
                        </Typography>
                    </div>
                    <Box display="flex" justifyContent="space-between">
                        <div>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                            >
                                <InputLabel id="per-page-select-label">
                                    Per Page
                                </InputLabel>
                                <Select
                                    labelId="per-page-select-label"
                                    id="perPage-select"
                                    value={perPage}
                                    onChange={handlePerPageChange}
                                    label="Per Page"
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={15}>15</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                    <MenuItem value={4000}>4000</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <Button
                            variant="outlined"
                            color="primary"
                            aria-controls="action-menu"
                            aria-haspopup="true"
                            onClick={handleActionClick}
                        >
                            Actions <ExpandMoreIcon />
                        </Button>
                        <Menu
                            id="action-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleActionClose}
                        >
                            <MenuItem onClick={onBtExport}>
                                <SaveAltIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Export to Excel
                            </MenuItem>
                            <MenuItem onClick={onBtnCSVExport}>
                                <SaveAltIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Export to CSV
                            </MenuItem>
                            <MenuItem onClick={onBtnCSVExportAll}>
                                <SaveAltIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Export All to CSV
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                <table
                    style={{
                        margin: "0 20px",
                        width: "calc(100% - 40px)",
                        height: "calc(100vh - 175px)",
                    }}
                >
                    <tbody>
                        <tr>
                            <td style={{ border: "1px solid #babfc7" }}>
                                <div
                                    className="ag-theme-alpine"
                                    style={{
                                        height: "100%",
                                        width: "100%",
                                    }}
                                >
                                    <AgGridReact
                                        ref={gridRef}
                                        columnDefs={columnDefs}
                                        defaultColDef={defaultColDef}
                                        onFirstDataRendered={
                                            onFirstDataRendered
                                        }
                                        pagination={true}
                                        rowModelType={"serverSide"}
                                        onGridReady={onGridReady}
                                        paginationPageSize={perPage}
                                        cacheBlockSize={perPage}
                                        paginationPageSizeSelector={[
                                            10, 15, 25, 50, 100, 4000,
                                        ]}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default RegistrationStatusReporting;
